import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalendar } from '../context/CalendarContext';
import { parseCourseSchedule } from '../utils/calendarUtils';

/**
 * Floating Calendar Widget (Simplified - Fixed Position)
 * Mini calendar that appears on Courses page after adding courses
 */
function FloatingCalendarWidget({ isVisible, onClose }) {
  const navigate = useNavigate();
  const { plannedCourses } = useCalendar();

  if (!isVisible || plannedCourses.length === 0) {
    return null;
  }

  // Mini calendar grid
  const days = ['M', 'T', 'W', 'Th', 'F'];
  const courseSchedules = plannedCourses.map(course => ({
    course,
    schedule: parseCourseSchedule(course)
  }));

  return (
    <div
      style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999 }}
      className="bg-white dark:bg-dark-200 rounded-xl shadow-2xl border-2 border-gray-300 dark:border-dark-400 w-80"
    >
      {/* Header */}
      <div className="bg-gray-50 dark:bg-dark-300 px-4 py-3 rounded-t-xl border-b border-gray-200 dark:border-dark-400 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-800">
            My Schedule
          </h3>
          <p className="text-xs text-gray-600 dark:text-dark-600">
            {plannedCourses.length} course{plannedCourses.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/calendar')}
            className="px-3 py-1 bg-accent-primary hover:bg-accent-secondary text-white text-xs font-medium rounded-lg transition-colors"
          >
            Open
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 dark:hover:bg-dark-400 rounded"
          >
            <svg className="w-4 h-4 text-gray-600 dark:text-dark-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mini Calendar Grid */}
      <div className="p-4">
        <div className="grid grid-cols-5 gap-px bg-gray-200 dark:bg-dark-400 rounded-lg overflow-hidden">
          {days.map((day, dayIndex) => (
            <div key={day} className="bg-white dark:bg-dark-200">
              <div className="text-center py-2 bg-gray-50 dark:bg-dark-300 text-xs font-medium text-gray-700 dark:text-dark-700">
                {day}
              </div>
              <div className="relative h-32">
                {courseSchedules.map(({ course, schedule }) => {
                  const dayMap = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
                  const daySchedule = schedule[dayMap[dayIndex]];
                  if (!daySchedule) return null;

                  return (
                    <div
                      key={course.class_tag}
                      className="absolute left-0 right-0 mx-0.5 my-0.5 bg-accent-primary/90 rounded px-1 py-0.5"
                      style={{ top: '20%', height: '30%' }}
                      title={`${course.class_tag}\n${daySchedule.start}-${daySchedule.end}`}
                    >
                      <div className="text-white text-xs font-medium truncate">
                        {course.class_tag}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Course list */}
        <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
          {plannedCourses.map(course => (
            <div
              key={course.class_tag}
              className="text-xs text-gray-700 dark:text-dark-700 flex items-center justify-between py-1 px-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded"
            >
              <span className="font-medium">{course.class_tag}</span>
              <span className="text-gray-500 dark:text-dark-600">{course.units || 4} units</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default FloatingCalendarWidget;